import { format } from '@formkit/tempo'
import { ITableColumn } from '@/components/UI/DataTable/TableHead'
import { IScoringDate } from '@/types'

// This function cleans any object, array by replacing any null values by empty string [excpetions are dates and statues]
// Helps avoid the need to place individual null checks to each and every property
// For the exceptions, it's better to add a conditional check in the same block and render empty string
type DeepNullable<T> = {
  [K in keyof T]: T[K] extends object ? DeepNullable<T[K]> : T[K] | null
}

type ReplaceNull<T> = T extends null
  ? ' '
  : T extends Array<infer U>
    ? Array<ReplaceNull<U>>
    : T extends object
      ? { [K in keyof T]: ReplaceNull<T[K]> }
      : T

export const formatDate = (
  dateString: string,
  showTimeBefore: boolean = true,
  showTimeAfter?: boolean
) => {
  if (!dateString) return ''
  const formatString = showTimeBefore
    ? 'hh:mm A MMM D, YYYY'
    : showTimeAfter
      ? 'MMM D, YYYY hh:mm A'
      : 'MMM D, YYYY'
  return format({ date: dateString, format: formatString, tz: 'UTC' })
}

// Formats a timestamp as the day-first numeric date AU readers expect. Pinned to UTC for the same
// reason formatDate is: the day a row shows must not change with the machine the panel runs on.
export const formatDayMonthYear = (dateString: string) => {
  if (!dateString) return ''
  return format({ date: dateString, format: 'DD/MM/YYYY', tz: 'UTC' })
}

// Formats the structured { year, month, day } scoring date into "MMM D, YYYY".
// Pinned to be fully machine-independent (it's shown to AU users from any machine):
//  - month is 1-indexed (proto google.type.Date), so it maps straight into the ISO string.
//  - "T00:00:00Z" + tz:'UTC' fix the day: a bare date-only string is parsed as local
//    midnight and the tz conversion would shift it back a day in zones ahead of UTC (AU).
//  - locale:'en' fixes the month name: without it tempo uses the machine/browser locale
//    (e.g. "Juni" in de, "6" in ja).
export const formatScoringDate = (date?: IScoringDate) => {
  if (!date?.year) return 'N/A'
  const iso = `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}T00:00:00Z`
  return format({ date: iso, format: 'MMM D, YYYY', tz: 'UTC', locale: 'en' })
}

// Shows a score without trailing zeros, keeping decimals only when meaningful
// (capped at 2dp): 325.00 -> "325", 325.27 -> "325.27", 325.2 -> "325.2".
export const formatScore = (value?: number) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—'
  return String(Number(value.toFixed(2)))
}

export const safeFormatDate = (
  dateString: string,
  formatString: string = 'hh:mm A MMM D, YYYY',
  locale: string = 'en'
): string => {
  if (!dateString || isNaN(Date.parse(dateString))) return 'N/A'
  try {
    return format(dateString, formatString, locale)
  } catch {
    return 'N/A'
  }
}

// source parameter is temporary, will update it in future as per the requirement.
export const transformedColumns = (columns: ITableColumn<any>[], source?: string) => {
  return columns.map(column => ({
    numeric: column.numeric ?? false,
    disablePadding: column.disablePadding ?? false,
    // Note that this should only be enabled when the relevant api endpoint provisions sorting and filtering capabilities
    enableSorting: (column.enableSorting ?? source === 'Customers') ? true : false,
    ...column,
  }))
}

export const capitalizeWords = (str: string) => {
  return str.replace(/\b\w/g, function (char) {
    return char.toUpperCase()
  })
}

export const replaceCharacterWithSpace = (
  str: string,
  char?: string,
  replaceChar: string = ' '
) => {
  if (!char) return str
  return str.split(char).join(replaceChar)
}

export const hexToRgba = (hex: string, alpha: number = 1): string => {
  const [red, green, blue] = hex.match(/\w\w/g)!.map(x => parseInt(x, 16))
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

export const replaceNullValues = <T extends object>(obj: DeepNullable<T>): ReplaceNull<T> => {
  if (typeof obj !== 'object' || obj === null) {
    return (obj === null ? ' ' : obj) as ReplaceNull<T>
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item =>
      item === null ? ' ' : typeof item === 'object' ? replaceNullValues(item as object) : item
    ) as ReplaceNull<T>
  }

  // Handle objects
  const result = {} as { [K in keyof T]: ReplaceNull<T[K]> }
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key]

      result[key as keyof T] = (
        value === null
          ? ' '
          : typeof value === 'object'
            ? replaceNullValues(value as object)
            : value
      ) as ReplaceNull<T[keyof T]>
    }
  }

  return result as ReplaceNull<T>
}

export async function downloadPDFFile(base64Data: string, fileName = 'agreement.pdf') {
  const base64String = base64Data.startsWith('data:') ? base64Data.split(',')[1] : base64Data

  const byteCharacters = atob(base64String)
  const byteArrays = []

  for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
    const slice = byteCharacters.slice(offset, offset + 1024)
    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }
    byteArrays.push(new Uint8Array(byteNumbers))
  }

  const blob = new Blob(byteArrays, { type: 'application/pdf' })
  const link = document.createElement('a')
  const urlBlob = URL.createObjectURL(blob)

  link.href = urlBlob
  link.download = fileName // Set the download attribute and the filename
  link.click() // Trigger the download by simulating a click event
  URL.revokeObjectURL(urlBlob) // Clean up the object URL
}

// Amounts always show two decimal places, so they line up and read the same across the panel.
// Anything that isn't a number (a placeholder, an empty value) is passed through untouched.
export const formatAmount = (value: unknown) => {
  const amount = typeof value === 'string' && value.trim() !== '' ? Number(value) : value
  if (typeof amount !== 'number' || Number.isNaN(amount)) return value == null ? '' : String(value)
  return amount.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export const formatCents = (valueInCents: number) =>
  (valueInCents / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })
